import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import Card from '../../components/common/Card';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db, storage } from '../../firebase/config';
import {
  doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc,
  query, orderBy, where, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IoSettingsOutline, IoChevronBack, IoChevronForward, IoCloseCircle, IoLocationOutline, IoChatbubbleOutline } from 'react-icons/io5';
import { getDistanceMeters } from '../../utils/geoUtils';
import { HiPlus } from 'react-icons/hi2';
import { MdDelete, MdDescription, MdOndemandVideo, MdPerson, MdPhone, MdFamilyRestroom } from 'react-icons/md';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { friendlyDate, friendlyTimestamp, toLocalDateStr } from '../../utils/dateFormat';
const TABS = ['게시판', '수강생', '출석', '수업자료', '수업과제', '시험', '수업영상', '수업'];
const ATTENDANCE_STATUS = { present: '출석', late: '지각', absent: '결석', excused: '사유' };
const ATT_COLORS = { present: '#22C55E', late: '#F59E0B', absent: '#EF4444', excused: '#6B7280' };
const DAYS_KR = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

const EXT_COLORS = {
  pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
  xls: '#22C55E', xlsx: '#22C55E', csv: '#22C55E',
  ppt: '#F97316', pptx: '#F97316',
  hwp: '#2563EB', hwpx: '#2563EB',
  jpg: '#A855F7', jpeg: '#A855F7', png: '#A855F7', gif: '#A855F7', webp: '#A855F7',
  mp4: '#3B82F6', avi: '#3B82F6', mov: '#3B82F6', mkv: '#3B82F6', webm: '#3B82F6',
  mp3: '#EC4899', wav: '#EC4899',
  zip: '#78716C', rar: '#78716C',
  txt: '#6B7280',
};
const getFileExt = (name) => {
  if (!name) return '';
  return name.toLowerCase().includes('.') ? name.toLowerCase().split('.').pop() : '';
};

const ClassDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { canEdit, canDelete, canUpload, canCheckAttendance, canGrade, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const targetUid = isParent ? (userData?.children?.[0] || userData?.uid) : userData?.uid;
  const [activeTab, setActiveTab] = useState(0);
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sub-collection states
  const [notices, setNotices] = useState([]);
  const [students, setStudents] = useState([]);
  const [tabLoaded, setTabLoaded] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [videos, setVideos] = useState([]);
  const [logs, setLogs] = useState([]);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);

  // 공유 자료실
  const [sharedMaterials, setSharedMaterials] = useState([]);
  const [sharedSearch, setSharedSearch] = useState('');

  // 학생 추가
  const [allStudentUsers, setAllStudentUsers] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentAdd, setShowStudentAdd] = useState(false);

  // Attendance
  const [calMonth, setCalMonth] = useState(new Date());
  const [selectedAttDate, setSelectedAttDate] = useState(null);
  const [attendanceMap, setAttendanceMap] = useState({});

  // Grading
  const [gradingAssignment, setGradingAssignment] = useState(null);
  const [gradeInputs, setGradeInputs] = useState({});
  const [expandedAssignment, setExpandedAssignment] = useState(null); // assignment id or null
  const [expandedGrade, setExpandedGrade] = useState(null); // grade id or null
  const [gradeScoreInputs, setGradeScoreInputs] = useState({});
  const [logForm, setLogForm] = useState({ date: toLocalDateStr(new Date()), content: '' });
  const [noteOpenSid, setNoteOpenSid] = useState(null); // sid with note input visible

  // 스케줄 관리
  const [newDay, setNewDay] = useState('월');
  const [newTime, setNewTime] = useState('09:00');
  const [supDate, setSupDate] = useState('');
  const [supTime, setSupTime] = useState('09:00');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showSupForm, setShowSupForm] = useState(false);

  const classRef = doc(db, 'classes', id);

  useEffect(() => {
    loadClassData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!classData) return;
    loadTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, classData]);

  const loadClassData = async () => {
    try {
      const snap = await getDoc(classRef);
      if (snap.exists()) {
        setClassData({ id: snap.id, ...snap.data() });
      }
    } catch (err) {
      console.error('클래스 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async () => {
    switch (activeTab) {
      case 0: await loadNotices(); break;
      case 1: await loadStudents(); break;
      case 2: if (students.length === 0) await loadStudents(); await loadAttendance(); break;
      case 3: await loadMaterials(); break;
      case 4: if (students.length === 0) await loadStudents(); await loadAssignments(); break;
      case 5: if (students.length === 0) await loadStudents(); await loadGrades(); break;
      case 6: await loadVideos(); break;
      case 7: await loadLogs(); break;
      default: break;
    }
  };

  const loadNotices = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'notices'), orderBy('createdAt', 'desc')));
    setNotices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, notices: true }));
  };

  const loadStudents = async () => {
    const studentIds = classData?.studentIds || [];
    if (studentIds.length === 0) { setStudents([]); setTabLoaded(prev => ({ ...prev, students: true })); return; }
    // 학생 정보 조회
    const studentList = [];
    for (const sid of studentIds) {
      try {
        const userDoc = await getDoc(doc(db, 'users', sid));
        if (userDoc.exists()) {
          studentList.push({ id: sid, ...userDoc.data() });
        } else {
          studentList.push({ id: sid, name: sid });
        }
      } catch { studentList.push({ id: sid, name: sid }); }
    }
    // 학부모 조회 - children 배열에 학생 id 또는 uid가 포함된 유저
    const allUsersSnap = await getDocs(collection(db, 'users'));
    // 학생의 모든 가능한 ID 수집 (문서 id + uid 필드)
    const allStudentKeys = new Set(studentIds);
    studentList.forEach(s => { if (s.uid) allStudentKeys.add(s.uid); });

    const parentMap = {}; // key -> [parent, ...]
    allUsersSnap.docs.forEach(d => {
      const data = d.data();
      if (data.role === 'parent' && data.children && data.children.length > 0) {
        data.children.forEach(childId => {
          if (allStudentKeys.has(childId)) {
            if (!parentMap[childId]) parentMap[childId] = [];
            parentMap[childId].push({ id: d.id, name: data.name, phone: data.phoneE164 || data.phone || '' });
          }
        });
      }
    });
    // 학생에 학부모 정보 합치기 (id, uid 모두 체크)
    const merged = studentList.map(s => {
      const parents = parentMap[s.id] || parentMap[s.uid] || [];
      return { ...s, parents };
    });
    setStudents(merged);
    setTabLoaded(prev => ({ ...prev, students: true }));
  };

  const loadAllStudentUsers = async () => {
    const snap = await getDocs(collection(db, 'users'));
    setAllStudentUsers(snap.docs.filter(d => d.data().role === 'student').map(d => ({ uid: d.id, ...d.data() })));
  };

  const addStudentToClass = async (uid) => {
    const currentIds = classData?.studentIds || [];
    if (currentIds.includes(uid)) return;
    const updated = [...currentIds, uid];
    await updateDoc(doc(db, 'classes', id), { studentIds: updated, students: updated.length });
    setClassData(prev => ({ ...prev, studentIds: updated, students: updated.length }));
    loadStudents();
  };

  const removeStudentFromClass = async (uid) => {
    const st = students.find(s => s.id === uid);
    const studentName = st?.name || uid;
    if (!window.confirm(`'${studentName}' 학생을 이 클래스에서 제거하시겠습니까?\n\n해당 클래스의 학생 관련 자료가 삭제됩니다.`)) return;
    const currentIds = classData?.studentIds || [];
    const updated = currentIds.filter(s => s !== uid);
    await updateDoc(doc(db, 'classes', id), { studentIds: updated, students: updated.length });
    setClassData(prev => ({ ...prev, studentIds: updated, students: updated.length }));
    loadStudents();
  };

  const filteredStudentUsers = allStudentUsers.filter(s => {
    if ((classData?.studentIds || []).includes(s.uid)) return false;
    if (!studentSearch) return true;
    return s.name?.includes(studentSearch);
  });

  const loadAttendance = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'attendance'), orderBy('date', 'desc')));
    setAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, attendance: true }));
  };

  const loadMaterials = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'materials')));
    setMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, materials: true }));
  };

  const loadAssignments = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'assignments'), orderBy('dueDate', 'desc')));
    setAssignments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, assignments: true }));
  };

  const loadGrades = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'grades'), orderBy('date', 'desc')));
    setGrades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, grades: true }));
  };

  const loadLogs = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'logs'), orderBy('date', 'desc')));
    setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, logs: true }));
  };

  const loadVideos = async () => {
    const snap = await getDocs(query(collection(db, 'classes', id, 'videos')));
    setVideos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setTabLoaded(prev => ({ ...prev, videos: true }));
  };

  // CRUD handlers
  const openModal = async (type) => {
    setModalType(type);
    setFormData({});
    setSharedSearch('');
    if (type === 'material' || type === 'video') {
      const snap = await getDocs(collection(db, 'materials'));
      setSharedMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    setShowModal(true);
  };

  const handleAddNotice = async () => {
    if (!formData.title || !formData.content) return;
    await addDoc(collection(db, 'classes', id, 'notices'), {
      title: formData.title,
      content: formData.content,
      authorId: currentUser.uid,
      authorName: userData?.name || '',
      isPinned: formData.isPinned || false,
      createdAt: serverTimestamp(),
    });
    setShowModal(false);
    loadNotices();
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'classes', id, 'notices', noticeId));
    loadNotices();
  };

  // 출석 - 날짜 선택 시 기존 데이터 불러오기
  const initAttendanceForDate = (dateStr) => {
    const studentIds = classData?.studentIds || [];
    const map = {};
    studentIds.forEach(sid => { map[sid] = { status: 'present', note: '' }; });
    const existing = attendance.find(r => r.date === dateStr);
    if (existing?.records) {
      Object.entries(existing.records).forEach(([sid, val]) => {
        if (typeof val === 'string') { map[sid] = { status: val, note: '' }; }
        else { map[sid] = val; }
      });
    }
    setAttendanceMap(map);
    setSelectedAttDate(dateStr);
  };

  const handleSaveAttendance = async () => {
    if (!selectedAttDate) return;
    const existing = attendance.find(a => a.date === selectedAttDate);
    if (existing) {
      await updateDoc(doc(db, 'classes', id, 'attendance', existing.id), { records: attendanceMap });
      setAttendance(attendance.map(r => r.id === existing.id ? { ...r, records: attendanceMap } : r));
    } else {
      const data = { date: selectedAttDate, type: 'regular', records: attendanceMap, createdAt: Timestamp.now() };
      const docRef = await addDoc(collection(db, 'classes', id, 'attendance'), data);
      setAttendance([{ id: docRef.id, ...data }, ...attendance]);
    }
    alert('출석이 저장되었습니다.');
  };

  // ─── 셀프 출석 (위치 기반) ───
  const [selfChecking, setSelfChecking] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [academySettings, setAcademySettings] = useState(null);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'academy')).then(snap => {
      if (snap.exists()) setAcademySettings(snap.data());
    }).catch(() => {});
  }, []);

  const getOpenSelfSession = () => {
    if (classData?.attendanceMode !== 'self') return null;
    const today = toLocalDateStr(new Date());
    return attendance.find(r => r.date === today && r.selfCheckOpen);
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleSelfCheck = async () => {
    const session = getOpenSelfSession();
    if (!session || !targetUid) return;
    const loc = classData?.location || academySettings?.location;
    const radius = classData?.locationRadius || academySettings?.locationRadius || 100;
    if (!loc?.lat || !loc?.lng) { showToast('학원 위치가 설정되지 않았습니다. 관리자에게 문의하세요.'); return; }

    const myRecord = session.records?.[targetUid];
    if (myRecord?.status === 'present' || myRecord?.status === 'late') {
      showToast('이미 출석 완료했습니다.'); return;
    }

    if (!navigator.geolocation) { showToast('위치를 확인할 수 없어 출석 체크를 할 수 없습니다.'); return; }

    setSelfChecking(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const dist = getDistanceMeters(loc.lat, loc.lng, pos.coords.latitude, pos.coords.longitude);
          if (dist > radius) {
            showToast(`학원 범위 밖입니다. (${Math.round(dist)}m / 허용 ${radius}m)`);
            setSelfChecking(false);
            return;
          }
          const newRecords = {
            ...session.records,
            [targetUid]: {
              status: 'present', note: '',
              checkedAt: Timestamp.now(),
              checkedLocation: { lat: pos.coords.latitude, lng: pos.coords.longitude },
            },
          };
          await updateDoc(doc(db, 'classes', id, 'attendance', session.id), { records: newRecords });
          setAttendance(attendance.map(r => r.id === session.id ? { ...r, records: newRecords } : r));
          showToast('출석 완료!');
        } catch (err) {
          console.error('셀프 출석 실패:', err);
          showToast('출석 처리 중 오류가 발생했습니다.');
        } finally {
          setSelfChecking(false);
        }
      },
      (err) => {
        setSelfChecking(false);
        if (err.code === 1) showToast('위치 권한을 허용해주세요. 설정에서 위치 접근을 허용해야 출석할 수 있습니다.');
        else showToast('위치를 확인할 수 없어 출석 체크를 할 수 없습니다.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const getStudentName = (sid) => {
    const s = students.find(st => st.id === sid || st.uid === sid);
    return s?.name || sid;
  };

  const getAttSummary = (dateStr) => {
    const record = attendance.find(r => r.date === dateStr);
    if (!record?.records) return null;
    const counts = { present: 0, late: 0, absent: 0, excused: 0 };
    Object.values(record.records).forEach(val => {
      const status = typeof val === 'string' ? val : val.status;
      if (counts[status] !== undefined) counts[status]++;
    });
    const total = counts.present + counts.late + counts.absent + counts.excused;
    if (total === 0) return null;
    return counts;
  };

  const getMyAttStatus = (dateStr) => {
    const record = attendance.find(r => r.date === dateStr);
    if (!record?.records) return null;
    const val = record.records[targetUid];
    if (!val) return null;
    return typeof val === 'string' ? { status: val, note: '' } : val;
  };

  // 캘린더 헬퍼
  const getCalendarDays = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const toDateStr = (dayNum) => {
    const y = calMonth.getFullYear();
    const m = String(calMonth.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-${String(dayNum).padStart(2, '0')}`;
  };

  const isScheduleDay = (dayNum) => {
    const dateStr = toDateStr(dayNum);
    const d = new Date(dateStr);
    const dayKr = DAYS_KR[d.getDay()];
    const schedules = classData?.schedule || [];
    const supplements = classData?.supplements || [];
    return schedules.some(s => s.day === dayKr) || supplements.some(s => s.date === dateStr);
  };

  const handleFileUpload = async (file, subCollection) => {
    if (!file) return null;
    setUploading(true);
    try {
      const storageRef = ref(storage, `classes/${id}/${subCollection}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } finally {
      setUploading(false);
    }
  };

  const handleAddMaterial = async () => {
    const selected = formData.selectedMaterials || [];
    if (selected.length === 0) return;
    for (const mat of selected) {
      await addDoc(collection(db, 'classes', id, 'materials'), {
        title: mat.title,
        fileUrl: mat.fileUrl || '',
        fileName: mat.fileName || '',
        fileType: mat.fileType || '',
        category: mat.category || '',
        sourceId: mat.id,
        uploadedBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
    }
    setShowModal(false);
    loadMaterials();
  };

  const handleAddAssignment = async () => {
    if (!formData.title || !formData.dueDate) return;
    await addDoc(collection(db, 'classes', id, 'assignments'), {
      title: formData.title,
      description: formData.description || '',
      dueDate: formData.dueDate,
      maxScore: Number(formData.maxScore) || 100,
      submissions: {},
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    setShowModal(false);
    loadAssignments();
  };

  const handleGradeSubmit = async () => {
    if (!gradingAssignment) return;
    const updatedSubmissions = { ...gradingAssignment.submissions };
    Object.entries(gradeInputs).forEach(([studentId, score]) => {
      if (score !== '' && score !== undefined) {
        updatedSubmissions[studentId] = {
          ...(updatedSubmissions[studentId] || {}),
          score: Number(score),
          gradedBy: currentUser.uid,
          gradedAt: new Date().toISOString(),
        };
      }
    });
    await updateDoc(doc(db, 'classes', id, 'assignments', gradingAssignment.id), {
      submissions: updatedSubmissions,
    });
    setGradingAssignment(null);
    setGradeInputs({});
    loadAssignments();
  };

  const handleAddVideo = async () => {
    const selected = formData.selectedMaterials || [];
    if (selected.length === 0) return;
    for (const mat of selected) {
      await addDoc(collection(db, 'classes', id, 'videos'), {
        title: mat.title,
        fileUrl: mat.fileUrl || '',
        videoUrl: mat.fileUrl || '',
        fileName: mat.fileName || '',
        fileType: mat.fileType || '',
        category: mat.category || '',
        sourceId: mat.id,
        uploadedBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
    }
    setShowModal(false);
    loadVideos();
  };

  const handleAddGrade = async () => {
    if (!formData.title || !formData.dueDate) return;
    await addDoc(collection(db, 'classes', id, 'grades'), {
      examName: formData.title,
      date: formData.dueDate,
      maxScore: Number(formData.maxScore) || 100,
      scores: {},
      createdBy: currentUser.uid,
      createdAt: Timestamp.now(),
    });
    setShowModal(false);
    setFormData({});
    loadGrades();
  };

  const handleAddLog = async () => {
    if (!logForm.content) return;
    await addDoc(collection(db, 'classes', id, 'logs'), {
      date: logForm.date,
      content: logForm.content,
      createdAt: Timestamp.now(),
    });
    setShowModal(false);
    setLogForm({ date: toLocalDateStr(new Date()), content: '' });
    loadLogs();
  };

  // 스케줄 관리
  const addSchedule = async () => {
    if (!newDay || !newTime) return;
    const updated = [...(classData.schedule || []), { day: newDay, time: newTime }];
    await updateDoc(classRef, { schedule: updated });
    setClassData(prev => ({ ...prev, schedule: updated }));
    setNewTime('09:00');
  };
  const removeSchedule = async (idx) => {
    if (!window.confirm('이 수업 시간을 삭제하시겠습니까?')) return;
    const updated = (classData.schedule || []).filter((_, i) => i !== idx);
    await updateDoc(classRef, { schedule: updated });
    setClassData(prev => ({ ...prev, schedule: updated }));
  };
  const addSupplement = async () => {
    if (!supDate || !supTime) return;
    const updated = [...(classData.supplements || []), { date: supDate, time: supTime }];
    await updateDoc(classRef, { supplements: updated });
    setClassData(prev => ({ ...prev, supplements: updated }));
    setSupTime('09:00');
    setSupDate('');
  };
  const removeSupplement = async (idx) => {
    if (!window.confirm('이 보강 수업을 삭제하시겠습니까?')) return;
    const updated = (classData.supplements || []).filter((_, i) => i !== idx);
    await updateDoc(classRef, { supplements: updated });
    setClassData(prev => ({ ...prev, supplements: updated }));
  };

  const handleDeleteItem = async (subCol, itemId) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'classes', id, subCol, itemId));
    loadTabData();
  };

  // ============ RENDER TABS ============

  const renderBoard = () => {
    const pinned = notices.filter(n => n.isPinned);
    const regular = notices.filter(n => !n.isPinned);

    return (
      <>
        {pinned.length > 0 && (
          <NoticeBox>
            <NoticeHeader>고정된 공지사항</NoticeHeader>
            {pinned.map(n => (
              <NoticeItem key={n.id}>
                <div>
                  <p>{n.title}</p>
                  <NoticeDate>{n.authorName} · {formatDate(n.createdAt)}</NoticeDate>
                </div>
                {canDelete && (
                  <DeleteBtn onClick={() => handleDeleteNotice(n.id)}><MdDelete size={18} /></DeleteBtn>
                )}
              </NoticeItem>
            ))}
          </NoticeBox>
        )}

        {regular.map(n => (
          <PostCard key={n.id}>
            <PostCardTop>
              <PostCardTitle>{n.title}</PostCardTitle>
              {canDelete && (
                <DeleteBtn onClick={() => handleDeleteNotice(n.id)}><MdDelete size={16} /></DeleteBtn>
              )}
            </PostCardTop>
            <PostCardContent>{n.content}</PostCardContent>
            <PostCardMeta>게시 {formatDate(n.createdAt)}</PostCardMeta>
          </PostCard>
        ))}

        {!tabLoaded.notices && <SpinnerWrap><Spinner /></SpinnerWrap>}
        {tabLoaded.notices && notices.length === 0 && <EmptyState><p>게시글이 없습니다.</p></EmptyState>}
      </>
    );
  };

  const renderAttendance = () => {
    const studentIds = classData?.studentIds || [];
    const today = toLocalDateStr(new Date());
    const calDays = getCalendarDays();
    const monthStr = `${calMonth.getFullYear()}년 ${calMonth.getMonth() + 1}월`;

    return (
      <>
        {/* Calendar */}
        <CalendarWrap>
          <CalMonthHeader>
            <CalNavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}>
              <IoChevronBack size={20} />
            </CalNavBtn>
            <CalMonthTitle>{monthStr}</CalMonthTitle>
            <CalNavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}>
              <IoChevronForward size={20} />
            </CalNavBtn>
          </CalMonthHeader>

          <CalWeekRow>
            {DAYS_KR.map((d, i) => (
              <CalWeekCell key={d} $sun={i === 0} $sat={i === 6}>{d}</CalWeekCell>
            ))}
          </CalWeekRow>

          <CalGrid>
            {calDays.map((dayNum, i) => {
              const colIdx = i % 7;
              if (!dayNum) return <CalDayCell key={i} />;
              const dateStr = toDateStr(dayNum);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedAttDate;
              const hasSchedule = isScheduleDay(dayNum);
              const summary = getAttSummary(dateStr);
              const myAtt = isChildView ? getMyAttStatus(dateStr) : null;

              return (
                <CalDayCell
                  key={i}
                  $selected={isSelected}
                  $hasSchedule={hasSchedule}
                  onClick={() => { if (hasSchedule && dateStr <= today) initAttendanceForDate(dateStr); }}
                >
                  <CalDayNum $today={isToday} $sun={colIdx === 0} $sat={colIdx === 6}>
                    {dayNum}
                  </CalDayNum>
                  {isChildView ? (
                    myAtt ? (
                      <CalSumText $color={ATT_COLORS[myAtt.status]}>{ATTENDANCE_STATUS[myAtt.status]}</CalSumText>
                    ) : hasSchedule && dateStr <= today ? (
                      <CalSumText $color={ATT_COLORS.absent}>결석</CalSumText>
                    ) : hasSchedule ? (
                      <CalDayDot $done={false} $future={true} />
                    ) : null
                  ) : (
                    summary ? (
                      <CalSummaryMini>
                        {summary.present > 0 && <CalSumText $color={ATT_COLORS.present}>출석{summary.present}</CalSumText>}
                        {summary.late > 0 && <CalSumText $color={ATT_COLORS.late}>지각{summary.late}</CalSumText>}
                        {summary.absent > 0 && <CalSumText $color={ATT_COLORS.absent}>결석{summary.absent}</CalSumText>}
                        {summary.excused > 0 && <CalSumText $color={ATT_COLORS.excused}>사유{summary.excused}</CalSumText>}
                      </CalSummaryMini>
                    ) : hasSchedule ? (
                      <CalDayDot $done={false} $future={dateStr > today} />
                    ) : null
                  )}
                </CalDayCell>
              );
            })}
          </CalGrid>
        </CalendarWrap>

        {/* Attendance Panel - 날짜 선택 시 */}
        {selectedAttDate && canCheckAttendance && (() => {
          const isFuture = selectedAttDate > today;
          // 학생은 자기만, 강사는 전체 학생
          const visibleIds = isChildView ? [targetUid].filter(uid => studentIds.includes(uid)) : studentIds;
          return (
            <AttPanel>
              <AttPanelHeader>
                <AttPanelTitle>{selectedAttDate} 출석</AttPanelTitle>
                <CalNavBtn onClick={() => setSelectedAttDate(null)}>✕</CalNavBtn>
              </AttPanelHeader>
              {isFuture ? (
                <AttFutureMsg>수업 전입니다.</AttFutureMsg>
              ) : isChildView ? (
                (() => {
                  const myAtt = getMyAttStatus(selectedAttDate);
                  const current = attendanceMap[targetUid] || { status: myAtt?.status || 'present', note: myAtt?.note || '' };
                  const isSelfMode = classData?.attendanceMode === 'self';
                  const isToday = selectedAttDate === today;
                  const selfCheckedIn = myAtt?.checkedAt;
                  const needsSelfCheck = isToday && isSelfMode && isStudent && !selfCheckedIn;
                  return (
                    <>
                      {needsSelfCheck ? (
                        <AttMyStatus $color="#9CA3AF">아직 출석 체크를 하지 않았습니다.</AttMyStatus>
                      ) : myAtt ? (
                        <AttMyStatus $color={ATT_COLORS[current.status]}>
                          {ATTENDANCE_STATUS[current.status]} 처리 되었습니다.
                          {current.note && <AttReasonText>{current.note}</AttReasonText>}
                        </AttMyStatus>
                      ) : (
                        <AttMyStatus $color={ATT_COLORS.absent}>결석 처리 되었습니다.</AttMyStatus>
                      )}
                      {myAtt?.status === 'excused' && (
                        <AttReasonInput
                          value={current.note}
                          onChange={e => setAttendanceMap(prev => ({ ...prev, [targetUid]: { ...current, note: e.target.value } }))}
                          rows={2}
                        />
                      )}
                      {needsSelfCheck && (
                        <SelfCheckBtn onClick={handleSelfCheck} disabled={selfChecking}>
                          <IoLocationOutline size={20} />
                          {selfChecking ? '위치 확인 중...' : '출석하기'}
                        </SelfCheckBtn>
                      )}
                      {current.status === 'excused' && !isParent && (
                        <AttSaveBtn onClick={handleSaveAttendance}>사유 저장</AttSaveBtn>
                      )}
                    </>
                  );
                })()
              ) : classData?.attendanceMode === 'self' ? (
                <>
                  <AttSelfModeNotice>위치 기반 출석 모드 (학생 직접 출석)</AttSelfModeNotice>
                  {visibleIds.map(sid => {
                    const current = attendanceMap[sid] || { status: 'absent', note: '' };
                    return (
                      <AttCard key={sid}>
                        <AttCardTop>
                          <AttCardNameRow>
                            <AttCardName>{getStudentName(sid)}</AttCardName>
                            <AttStatusBadge $color={ATT_COLORS[current.status]}>
                              {ATTENDANCE_STATUS[current.status]}
                            </AttStatusBadge>
                          </AttCardNameRow>
                          {current.note && <AttReadonlyNote>{current.note}</AttReadonlyNote>}
                        </AttCardTop>
                      </AttCard>
                    );
                  })}
                </>
              ) : (
                <>
                  {visibleIds.map(sid => {
                    const current = attendanceMap[sid] || { status: 'present', note: '' };
                    return (
                      <AttCard key={sid}>
                        <AttCardTop>
                          <AttCardNameRow>
                            <AttCardName>{getStudentName(sid)}</AttCardName>
                            <AttNoteToggle
                              $hasNote={!!current.note}
                              onClick={() => setNoteOpenSid(noteOpenSid === sid ? null : sid)}
                            >
                              {current.note ? '메모' : '메모'}
                            </AttNoteToggle>
                          </AttCardNameRow>
                          <AttStatusRow>
                            {Object.entries(ATTENDANCE_STATUS).map(([key, label]) => (
                              <AttStatusBtn
                                key={key}
                                $active={current.status === key}
                                $color={ATT_COLORS[key]}
                                onClick={() => setAttendanceMap(prev => ({ ...prev, [sid]: { ...current, status: key } }))}
                              >{label}</AttStatusBtn>
                            ))}
                          </AttStatusRow>
                        </AttCardTop>
                        {noteOpenSid === sid && (
                          <AttNoteInput
                            placeholder=""
                            value={current.note}
                            autoFocus
                            onChange={e => setAttendanceMap(prev => ({ ...prev, [sid]: { ...current, note: e.target.value } }))}
                          />
                        )}
                      </AttCard>
                    );
                  })}
                  <AttSaveBtn onClick={handleSaveAttendance}>출석 저장</AttSaveBtn>
                </>
              )}
            </AttPanel>
          );
        })()}

        {/* 셀프 출석 버튼 (학생용) */}
        {isStudent && (() => {
          const session = getOpenSelfSession();
          if (!session) return null;
          const myRecord = session.records?.[targetUid];
          const alreadyChecked = myRecord?.status === 'present' || myRecord?.status === 'late';
          return (
            <SelfCheckBanner>
              {alreadyChecked ? (
                <SelfCheckDone>오늘 출석 완료</SelfCheckDone>
              ) : (
                <SelfCheckBtn onClick={handleSelfCheck} disabled={selfChecking}>
                  <IoLocationOutline size={20} />
                  {selfChecking ? '위치 확인 중...' : '출석하기'}
                </SelfCheckBtn>
              )}
            </SelfCheckBanner>
          );
        })()}

        {!tabLoaded.attendance && <SpinnerWrap><Spinner /></SpinnerWrap>}
        {tabLoaded.attendance && !selectedAttDate && attendance.length === 0 && !getOpenSelfSession() && <EmptyState><p>출석 기록이 없습니다. 캘린더에서 수업일을 탭하세요.</p></EmptyState>}

        {toastMsg && <ToastOverlay><ToastBox>{toastMsg}</ToastBox></ToastOverlay>}
      </>
    );
  };

  const isVideo = (item) => (item.fileType || '').startsWith('video/');

  const renderMaterials = () => (
    <>
      {materials.map(item => (
        <Card key={item.id} $mb="12px" $padding="16px">
          <MaterialRow>
            <MaterialIcon
              as={item.fileUrl ? 'a' : 'div'}
              href={item.fileUrl || undefined}
              target={item.fileUrl ? '_blank' : undefined}
              rel={item.fileUrl ? 'noopener noreferrer' : undefined}
            >
              {getFileExt(item.fileName) ? (
                <FileIconBody>
                  <FileIconFold />
                  <FileIconExt $color={EXT_COLORS[getFileExt(item.fileName)] || '#9CA3AF'}>
                    {getFileExt(item.fileName).toUpperCase()}
                  </FileIconExt>
                </FileIconBody>
              ) : (
                <MdDescription size={48} color="#FB8C00" />
              )}
            </MaterialIcon>
            <MaterialInfo>
              {item.category && <CategoryLabel>[{item.category}]</CategoryLabel>}
              <MaterialTitle>{item.title}</MaterialTitle>
              <MaterialMeta>{formatDate(item.createdAt)}{item.fileName ? ` · ${item.fileName}` : ''}</MaterialMeta>
            </MaterialInfo>
            {canDelete && (
              <DeleteBtn onClick={() => handleDeleteItem('materials', item.id)}><MdDelete size={18} /></DeleteBtn>
            )}
          </MaterialRow>
        </Card>
      ))}
      {!tabLoaded.materials && <SpinnerWrap><Spinner /></SpinnerWrap>}
      {tabLoaded.materials && materials.length === 0 && <EmptyState><p>자료가 없습니다.</p></EmptyState>}
    </>
  );

  // 학생 과제 제출 상태
  const [classSubmitItem, setClassSubmitItem] = useState(null);
  const [classSubmitText, setClassSubmitText] = useState('');
  const [classSubmitFile, setClassSubmitFile] = useState(null);
  const [classSubmitting, setClassSubmitting] = useState(false);

  const renderAssignments = () => (
    <>
      {assignments.map(item => {
        const submissions = item.submissions || {};
        const studentIds = classData?.studentIds || [];
        const submittedCount = Object.values(submissions).filter(s => s.submitted).length;
        const ungradedCount = Object.values(submissions).filter(s => s.submitted && (s.score === undefined || s.score === null)).length;

        if (isChildView) {
          // 학생/학부모 뷰: 제출 상태
          const mySub = submissions[targetUid];
          const submitted = mySub?.submitted;
          const graded = submitted && mySub?.score !== undefined && mySub?.score !== null;
          return (
            <Card key={item.id} $mb="12px" $padding="16px">
              <AssignmentTop>
                <AssignmentTitle>{item.title}</AssignmentTitle>
                <Badge $variant={graded ? 'completed' : submitted ? 'inProgress' : 'notSubmitted'}>
                  {graded ? '채점완료' : submitted ? '제출완료' : '미제출'}
                </Badge>
              </AssignmentTop>
              {item.description && <AssignmentDesc>{item.description}</AssignmentDesc>}
              <AssignmentMeta>마감: {friendlyDate(item.dueDate)}</AssignmentMeta>
              {graded && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--color-primary-light)', borderRadius: 8, fontSize: 14 }}>
                  <strong style={{ color: 'var(--color-primary)' }}>점수: {mySub.score}</strong> / {item.maxScore || 100}
                </div>
              )}
              {submitted && (mySub.text || mySub.fileUrl) && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, fontSize: 13, color: '#374151' }}>
                  {mySub.text && <div>제출 내용: {mySub.text}</div>}
                  {mySub.fileUrl && (
                    <a href={mySub.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', fontSize: 13 }}>
                      첨부: {mySub.fileName || '파일 보기'}
                    </a>
                  )}
                </div>
              )}
              {!submitted && isStudent && (
                <GradeButton onClick={() => { setClassSubmitItem(item); setClassSubmitText(''); setClassSubmitFile(null); }}>
                  과제 제출하기
                </GradeButton>
              )}
            </Card>
          );
        }

        // 강사 뷰 (기존)
        return (
          <Card key={item.id} $mb="12px" $padding="16px">
            <AssignmentTop>
              <AssignmentTitle>{item.title}</AssignmentTitle>
              {canDelete && (
                <DeleteBtn onClick={() => handleDeleteItem('assignments', item.id)}><MdDelete size={18} /></DeleteBtn>
              )}
            </AssignmentTop>
            {item.description && <AssignmentDesc>{item.description}</AssignmentDesc>}
            <AssignmentMeta>마감: {friendlyDate(item.dueDate)}</AssignmentMeta>
            <SubmissionStats>
              <SubmissionChip
                $active={expandedAssignment === item.id}
                $color="#22C55E"
                onClick={() => setExpandedAssignment(expandedAssignment === item.id ? null : item.id)}
              >
                제출 {submittedCount}/{studentIds.length}명 {expandedAssignment === item.id ? '▲' : '▼'}
              </SubmissionChip>
              {ungradedCount > 0 && <Badge $variant="urgent">미채점 {ungradedCount}</Badge>}
            </SubmissionStats>
            {expandedAssignment === item.id && (() => {
              const sids = classData?.studentIds || [];
              const subs = item.submissions || {};
              const submittedList = sids.filter(sid => subs[sid]?.submitted);
              const notSubmittedList = sids.filter(sid => !subs[sid]?.submitted);
              return (
                <SubmissionDetail>
                  {submittedList.length > 0 && (
                    <SubmissionGroup>
                      <SubmissionGroupLabel $color="#22C55E">제출 ({submittedList.length})</SubmissionGroupLabel>
                      {submittedList.map(sid => (
                        <div key={sid}>
                          <SubmissionRow>
                            <span>{getStudentName(sid)}</span>
                            <SubmissionStatus $color="#22C55E">
                              제출완료
                              {subs[sid]?.score !== undefined && subs[sid]?.score !== null && ` · ${subs[sid].score}점`}
                            </SubmissionStatus>
                          </SubmissionRow>
                          {(subs[sid]?.text || subs[sid]?.fileUrl) && (
                            <div style={{ padding: '2px 0 8px 8px', fontSize: 12, color: '#6B7280' }}>
                              {subs[sid].text && <div>{subs[sid].text}</div>}
                              {subs[sid].fileUrl && (
                                <a href={subs[sid].fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', fontSize: 12 }}>
                                  {subs[sid].fileName || '첨부파일'}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </SubmissionGroup>
                  )}
                  {notSubmittedList.length > 0 && (
                    <SubmissionGroup>
                      <SubmissionGroupLabel $color="#EF4444">미제출 ({notSubmittedList.length})</SubmissionGroupLabel>
                      {notSubmittedList.map(sid => (
                        <SubmissionRow key={sid}>
                          <span>{getStudentName(sid)}</span>
                          <SubmissionStatus $color="#EF4444">미제출</SubmissionStatus>
                        </SubmissionRow>
                      ))}
                    </SubmissionGroup>
                  )}
                </SubmissionDetail>
              );
            })()}
            {canGrade && ungradedCount > 0 && (
              <GradeButton onClick={() => {
                setGradingAssignment(item);
                const inputs = {};
                Object.entries(submissions).forEach(([sid, sub]) => {
                  if (sub.submitted) inputs[sid] = sub.score ?? '';
                });
                setGradeInputs(inputs);
              }}>
                채점하기
              </GradeButton>
            )}
          </Card>
        );
      })}

      {gradingAssignment && (
        <GradingOverlay onClick={() => setGradingAssignment(null)}>
          <GradingModal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{gradingAssignment.title} 채점</ModalTitle>
              <CloseBtn onClick={() => setGradingAssignment(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              {Object.entries(gradingAssignment.submissions || {}).filter(([, sub]) => sub.submitted).map(([sid, sub]) => (
                <div key={sid}>
                  <GradeRow>
                    <span>{sid}</span>
                    <GradeInput
                      type="number"
                      min="0"
                      max={gradingAssignment.maxScore || 100}
                      value={gradeInputs[sid] ?? ''}
                      onChange={e => setGradeInputs(prev => ({ ...prev, [sid]: e.target.value }))}
                      placeholder=""
                    />
                    <span>/ {gradingAssignment.maxScore || 100}</span>
                  </GradeRow>
                  {(sub.text || sub.fileUrl) && (
                    <div style={{ padding: '6px 0 12px', fontSize: 13, color: '#6B7280' }}>
                      {sub.text && <div style={{ marginBottom: 4 }}>내용: {sub.text}</div>}
                      {sub.fileUrl && (
                        <a href={sub.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                          첨부: {sub.fileName || '파일 보기'}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <ModalButtons>
                <CancelBtn onClick={() => setGradingAssignment(null)}>취소</CancelBtn>
                <SubmitBtn onClick={handleGradeSubmit}>저장</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </GradingModal>
        </GradingOverlay>
      )}

      {/* 학생 과제 제출 모달 */}
      {classSubmitItem && (
        <GradingOverlay onClick={() => setClassSubmitItem(null)}>
          <GradingModal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{classSubmitItem.title} 제출</ModalTitle>
              <CloseBtn onClick={() => setClassSubmitItem(null)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>과제 내용</FieldLabel>
              <textarea
                style={{ width: '100%', padding: 12, border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, minHeight: 100, resize: 'vertical' }}
                placeholder=""
                value={classSubmitText}
                onChange={e => setClassSubmitText(e.target.value)}
              />
              <FieldLabel>파일 첨부</FieldLabel>
              <div style={{ position: 'relative', marginTop: 4 }}>
                <input
                  type="file"
                  id="classSubmitFile"
                  style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
                  onChange={e => setClassSubmitFile(e.target.files[0] || null)}
                />
                <label
                  htmlFor="classSubmitFile"
                  style={{ display: 'block', padding: 12, border: '1px dashed #E5E7EB', borderRadius: 8, fontSize: 13, color: '#9CA3AF', textAlign: 'center', cursor: 'pointer' }}
                >
                  {classSubmitFile ? classSubmitFile.name : '파일을 선택하세요'}
                </label>
              </div>
              <ModalButtons>
                <CancelBtn onClick={() => setClassSubmitItem(null)}>취소</CancelBtn>
                <SubmitBtn disabled={classSubmitting} onClick={async () => {
                  if (!classSubmitText.trim() && !classSubmitFile) return;
                  setClassSubmitting(true);
                  try {
                    let fileUrl = null;
                    let fileName = null;
                    if (classSubmitFile) {
                      const storageRef = ref(storage, `assignments/${id}/${classSubmitItem.id}/${userData.uid}_${Date.now()}_${classSubmitFile.name}`);
                      await uploadBytes(storageRef, classSubmitFile);
                      fileUrl = await getDownloadURL(storageRef);
                      fileName = classSubmitFile.name;
                    }
                    const updatedSubs = { ...(classSubmitItem.submissions || {}) };
                    updatedSubs[userData.uid] = {
                      submitted: true,
                      text: classSubmitText.trim(),
                      submittedAt: new Date().toISOString(),
                      ...(fileUrl && { fileUrl, fileName }),
                    };
                    await updateDoc(doc(db, 'classes', id, 'assignments', classSubmitItem.id), {
                      submissions: updatedSubs,
                    });
                    setClassSubmitItem(null);
                    setClassSubmitText('');
                    setClassSubmitFile(null);
                    loadAssignments();
                  } catch (err) {
                    console.error('제출 실패:', err);
                  } finally {
                    setClassSubmitting(false);
                  }
                }}>{classSubmitting ? '제출 중...' : '제출'}</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </GradingModal>
        </GradingOverlay>
      )}

      {!tabLoaded.assignments && <SpinnerWrap><Spinner /></SpinnerWrap>}
      {tabLoaded.assignments && assignments.length === 0 && <EmptyState><p>수업과제가 없습니다.</p></EmptyState>}
    </>
  );

  const renderVideos = () => (
    <>
      {videos.map(item => (
        <Card key={item.id} $mb="12px" $padding="16px">
          <MaterialRow>
            <MaterialIcon
              as={item.fileUrl || item.videoUrl ? 'a' : 'div'}
              href={item.fileUrl || item.videoUrl || undefined}
              target={item.fileUrl || item.videoUrl ? '_blank' : undefined}
              rel={item.fileUrl || item.videoUrl ? 'noopener noreferrer' : undefined}
            >
              {getFileExt(item.fileName) ? (
                <FileIconBody>
                  <FileIconFold />
                  <FileIconExt $color={EXT_COLORS[getFileExt(item.fileName)] || '#3B82F6'}>
                    {getFileExt(item.fileName).toUpperCase()}
                  </FileIconExt>
                </FileIconBody>
              ) : (
                <MdOndemandVideo size={48} color="#1E88E5" />
              )}
            </MaterialIcon>
            <MaterialInfo>
              <MaterialTitle>{item.title}</MaterialTitle>
              <MaterialMeta>{item.duration && `${item.duration} · `}{formatDate(item.createdAt)}</MaterialMeta>
            </MaterialInfo>
            {canDelete && (
              <DeleteBtn onClick={() => handleDeleteItem('videos', item.id)}><MdDelete size={18} /></DeleteBtn>
            )}
          </MaterialRow>
        </Card>
      ))}
      {!tabLoaded.videos && <SpinnerWrap><Spinner /></SpinnerWrap>}
      {tabLoaded.videos && videos.length === 0 && <EmptyState><p>영상이 없습니다.</p></EmptyState>}
    </>
  );

  const handleSaveGradeScores = async (gradeId) => {
    const inputs = gradeScoreInputs[gradeId] || {};
    const scores = {};
    Object.entries(inputs).forEach(([sid, val]) => {
      if (val !== '' && val !== 'absent') scores[sid] = Number(val);
    });
    await updateDoc(doc(db, 'classes', id, 'grades', gradeId), { scores });
    setExpandedGrade(null);
    loadGrades();
  };

  const initGradeInputs = (grade) => {
    const studentIds = classData?.studentIds || [];
    const inputs = {};
    studentIds.forEach(sid => {
      const score = (grade.scores || {})[sid];
      if (score !== undefined && score !== null) {
        inputs[sid] = String(score);
      } else {
        inputs[sid] = 'absent';
      }
    });
    setGradeScoreInputs(prev => ({ ...prev, [grade.id]: inputs }));
  };

  const renderGrades = () => {
    const studentIds = classData?.studentIds || [];
    return (
      <>
        {grades.map(grade => {
          const scores = Object.entries(grade.scores || {});
          const scoreValues = scores.map(([, v]) => Number(v)).filter(v => !isNaN(v));
          const avg = scoreValues.length > 0 ? Math.round(scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length * 10) / 10 : 0;
          const max = scoreValues.length > 0 ? Math.max(...scoreValues) : 0;
          const min = scoreValues.length > 0 ? Math.min(...scoreValues) : 0;
          const isExpanded = expandedGrade === grade.id;
          const currentInputs = gradeScoreInputs[grade.id] || {};

          if (isChildView) {
            // 학생/학부모 뷰: 점수 + 전체 통계
            const myScore = (grade.scores || {})[targetUid];
            return (
              <Card key={grade.id} $mb="12px" $padding="16px">
                <AssignmentTop>
                  <AssignmentTitle>{grade.examName}</AssignmentTitle>
                </AssignmentTop>
                <AssignmentMeta>날짜: {friendlyDate(grade.date)} · 만점: {grade.maxScore || 100}점</AssignmentMeta>
                <GradeStatsRow>
                  <GradeStatBox $bg="var(--color-primary-light)">
                    <GradeStatValue style={{ color: 'var(--color-primary)' }}>{myScore !== undefined ? myScore : '-'}</GradeStatValue>
                    <GradeStatLabel>내 점수</GradeStatLabel>
                  </GradeStatBox>
                  <GradeStatBox $bg="#ECFDF5">
                    <GradeStatValue>{avg}</GradeStatValue>
                    <GradeStatLabel>평균</GradeStatLabel>
                  </GradeStatBox>
                  <GradeStatBox $bg="#FFF1F2">
                    <GradeStatValue>{max}</GradeStatValue>
                    <GradeStatLabel>최고</GradeStatLabel>
                  </GradeStatBox>
                  <GradeStatBox $bg="#EFF6FF">
                    <GradeStatValue>{scoreValues.length}명</GradeStatValue>
                    <GradeStatLabel>응시</GradeStatLabel>
                  </GradeStatBox>
                </GradeStatsRow>
              </Card>
            );
          }

          // 강사 뷰 (기존)
          return (
            <Card key={grade.id} $mb="12px" $padding="16px">
              <AssignmentTop>
                <AssignmentTitle>{grade.examName}</AssignmentTitle>
                {canDelete && (
                  <DeleteBtn onClick={() => handleDeleteItem('grades', grade.id)}><MdDelete size={18} /></DeleteBtn>
                )}
              </AssignmentTop>
              <AssignmentMeta>날짜: {friendlyDate(grade.date)} · 만점: {grade.maxScore || 100}점</AssignmentMeta>
              <GradeStatsRow>
                <GradeStatBox $bg="var(--color-primary-light)">
                  <GradeStatValue>{avg}</GradeStatValue>
                  <GradeStatLabel>평균</GradeStatLabel>
                </GradeStatBox>
                <GradeStatBox $bg="#ECFDF5">
                  <GradeStatValue>{max}</GradeStatValue>
                  <GradeStatLabel>최고</GradeStatLabel>
                </GradeStatBox>
                <GradeStatBox $bg="#FFF1F2">
                  <GradeStatValue>{min}</GradeStatValue>
                  <GradeStatLabel>최저</GradeStatLabel>
                </GradeStatBox>
                <GradeStatBox $bg="#EFF6FF">
                  <GradeStatValue>{scoreValues.length}/{studentIds.length}</GradeStatValue>
                  <GradeStatLabel>응시</GradeStatLabel>
                </GradeStatBox>
              </GradeStatsRow>
              <div style={{ marginTop: 8 }}>
                <SubmissionChip
                  $active={isExpanded}
                  $color="var(--color-primary)"
                  onClick={() => {
                    if (!isExpanded) initGradeInputs(grade);
                    setExpandedGrade(isExpanded ? null : grade.id);
                  }}
                >
                  학생별 점수 {isExpanded ? '▲' : '▼'}
                </SubmissionChip>
              </div>
              {isExpanded && (
                <SubmissionDetail style={{ marginTop: 10 }}>
                  {studentIds.map(sid => {
                    const val = currentInputs[sid] ?? 'absent';
                    const isAbsent = val === 'absent';
                    return (
                      <ScoreRow key={sid}>
                        <span>{getStudentName(sid)}</span>
                        <ScoreInputGroup>
                          <ScoreInput
                            type="number"
                            placeholder=""
                            value={isAbsent ? '' : val}
                            disabled={isAbsent}
                            onChange={e => setGradeScoreInputs(prev => ({
                              ...prev,
                              [grade.id]: { ...(prev[grade.id] || {}), [sid]: e.target.value }
                            }))}
                          />
                          <AbsentBtn
                            $active={isAbsent}
                            onClick={() => setGradeScoreInputs(prev => ({
                              ...prev,
                              [grade.id]: { ...(prev[grade.id] || {}), [sid]: isAbsent ? '' : 'absent' }
                            }))}
                          >미응시</AbsentBtn>
                        </ScoreInputGroup>
                      </ScoreRow>
                    );
                  })}
                  {canGrade && (
                    <SaveScoreBtnWrap><SaveScoreBtn onClick={() => handleSaveGradeScores(grade.id)}>저장</SaveScoreBtn></SaveScoreBtnWrap>
                  )}
                </SubmissionDetail>
              )}
            </Card>
          );
        })}
        {!tabLoaded.grades && <SpinnerWrap><Spinner /></SpinnerWrap>}
        {tabLoaded.grades && grades.length === 0 && <EmptyState><p>등록된 시험이 없습니다.</p></EmptyState>}
      </>
    );
  };

  const getLogScheduleInfo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const dow = DAYS_KR[d.getDay()];
    const schedules = classData?.schedule || [];
    const matched = schedules.find(s => s.day === dow);
    const time = matched?.time || '';
    return `${friendlyDate(dateStr)}${time ? ` ${time}` : ''}`;
  };

  const renderLogs = () => (
    <>
      {/* 수업 시간 관리 */}
      <Card $mb="12px" $padding="16px">
        <ScheduleSectionTitle>정규 수업</ScheduleSectionTitle>
        <ChipList>
          {(classData?.schedule || []).map((s, i) => (
            <ScheduleChip key={i}>
              {s.day} {s.time}
              {canEdit && <ChipRemoveBtn onClick={() => removeSchedule(i)}><IoCloseCircle size={16} /></ChipRemoveBtn>}
            </ScheduleChip>
          ))}
          {canEdit && <ScheduleAddChip onClick={() => setShowScheduleForm(v => !v)}><HiPlus size={14} /> 추가</ScheduleAddChip>}
        </ChipList>
        {!(classData?.schedule || []).length && !canEdit && (
          <ScheduleEmpty>등록된 정규 수업이 없습니다</ScheduleEmpty>
        )}
        {canEdit && showScheduleForm && (
          <ScheduleAddRow>
            <ScheduleSelect value={newDay} onChange={e => setNewDay(e.target.value)}>
              {WEEKDAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </ScheduleSelect>
            <ScheduleInput type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
            <ScheduleAddBtn onClick={() => { addSchedule(); setShowScheduleForm(false); }} disabled={!newTime}>추가</ScheduleAddBtn>
          </ScheduleAddRow>
        )}

        <ScheduleDivider />

        <ScheduleSectionTitle>보강 수업</ScheduleSectionTitle>
        <ChipList>
          {(classData?.supplements || []).filter(s => s.date >= toLocalDateStr(new Date())).map((s, i) => (
            <ScheduleChip key={i} $supplement>
              {friendlyDate(s.date)} {s.time}{s.endTime ? ` - ${s.endTime}` : ''}
              {canEdit && <ChipRemoveBtn onClick={() => {
                const origIdx = (classData?.supplements || []).indexOf(s);
                removeSupplement(origIdx);
              }}><IoCloseCircle size={16} /></ChipRemoveBtn>}
            </ScheduleChip>
          ))}
          {canEdit && <ScheduleAddChip onClick={() => setShowSupForm(v => !v)}><HiPlus size={14} /> 추가</ScheduleAddChip>}
        </ChipList>
        {(classData?.supplements || []).filter(s => s.date >= toLocalDateStr(new Date())).length === 0 && !canEdit && (
          <ScheduleEmpty>예정된 보강 수업이 없습니다</ScheduleEmpty>
        )}
        {canEdit && showSupForm && (
          <ScheduleAddRow>
            <ScheduleDateInput type="date" value={supDate} onChange={e => setSupDate(e.target.value)} placeholder="" />
            <ScheduleInput type="time" value={supTime} onChange={e => setSupTime(e.target.value)} />
            <ScheduleAddBtn onClick={() => { addSupplement(); setShowSupForm(false); }} disabled={!supDate || !supTime}>추가</ScheduleAddBtn>
          </ScheduleAddRow>
        )}
      </Card>

      {/* 수업 일지 */}
      {logs.length > 0 && <ScheduleSectionTitle style={{ padding: '8px 0 4px' }}>수업 일지</ScheduleSectionTitle>}
      {logs.map(log => (
        <Card key={log.id} $mb="12px" $padding="16px">
          <LogHeader>
            <LogDate>{getLogScheduleInfo(log.date)}</LogDate>
            {canDelete && (
              <DeleteBtn onClick={() => handleDeleteItem('logs', log.id)}><MdDelete size={18} /></DeleteBtn>
            )}
          </LogHeader>
          <LogContent>{log.content}</LogContent>
        </Card>
      ))}
      {!tabLoaded.logs && <SpinnerWrap><Spinner /></SpinnerWrap>}
      {tabLoaded.logs && logs.length === 0 && <EmptyState><p>수업일지가 없습니다.</p></EmptyState>}
    </>
  );

  // 채팅 시작 (기존 방 있으면 재사용)
  const startChatWith = async (partnerId, partnerName, partnerRole) => {
    const myUid = userData?.uid;
    if (!myUid) return;
    try {
      const q2 = query(collection(db, 'chatRooms'), where('participants', 'array-contains', myUid));
      const snap = await getDocs(q2);
      const existing = snap.docs.find(d => {
        const data = d.data();
        return data.participants.includes(partnerId) && data.participants.length === 2;
      });
      if (existing) { navigate(`/chat/${existing.id}`); return; }
      const newRoom = await addDoc(collection(db, 'chatRooms'), {
        participants: [myUid, partnerId],
        participantNames: { [myUid]: userData?.name || '', [partnerId]: partnerName || '' },
        participantRoles: { [myUid]: userData?.role || '', [partnerId]: partnerRole || '' },
        lastMessage: '', lastMessageAt: serverTimestamp(), createdAt: serverTimestamp(),
        unreadCount: { [myUid]: 0, [partnerId]: 0 },
      });
      navigate(`/chat/${newRoom.id}`);
    } catch (err) { console.error('채팅 시작 실패:', err); }
  };

  const renderStudents = () => (
    <>
      <StudentTopRow>
        <StudentCount>{students.length}명의 수강생</StudentCount>
        {canEdit && (
          <AddStudentBtn onClick={() => {
            if (!showStudentAdd) loadAllStudentUsers();
            setShowStudentAdd(!showStudentAdd);
            setStudentSearch('');
          }}>
            {showStudentAdd ? '닫기' : '+ 학생 추가'}
          </AddStudentBtn>
        )}
      </StudentTopRow>

      {showStudentAdd && (
        <StudentAddSection>
          <StudentSearchInput
            placeholder="학생 이름 검색..."
            value={studentSearch}
            onChange={e => setStudentSearch(e.target.value)}
          />
          <StudentAddList>
            {filteredStudentUsers.map(s => (
              <StudentAddRow key={s.uid} onClick={() => addStudentToClass(s.uid)}>
                <StudentAddInfo>
                  <StudentAddName>{s.name}</StudentAddName>
                  <StudentAddMeta>
                    {s.school && `${s.school}`}{s.grade && ` ${s.grade}`}
                  </StudentAddMeta>
                </StudentAddInfo>
                <HiPlus size={18} color="var(--color-primary)" />
              </StudentAddRow>
            ))}
            {filteredStudentUsers.length === 0 && (
              <StudentAddEmpty>{studentSearch ? '검색 결과가 없습니다' : '추가할 학생이 없습니다'}</StudentAddEmpty>
            )}
          </StudentAddList>
        </StudentAddSection>
      )}

      {students.map(s => (
        <StudentCard key={s.id}>
          <StudentHeader>
            {(s.photoURL || s.profileImage) ? (
              <StudentAvatar src={s.photoURL || s.profileImage} alt="" />
            ) : (
              <StudentAvatarDefault><MdPerson size={22} color="white" /></StudentAvatarDefault>
            )}
            <StudentInfo>
              <StudentName>{s.name || s.id}</StudentName>
              <StudentMeta>
                {s.school && `${s.school}`}{s.grade && ` ${s.grade}`}
              </StudentMeta>
            </StudentInfo>
            <StudentChatBtn onClick={(e) => { e.stopPropagation(); startChatWith(s.id, s.name, 'student'); }}>
              <IoChatbubbleOutline size={18} />
            </StudentChatBtn>
            {canEdit && (
              <StudentRemoveBtn onClick={() => removeStudentFromClass(s.id)}>
                제거
              </StudentRemoveBtn>
            )}
          </StudentHeader>
          {s.parents && s.parents.length > 0 && (
            <ParentSection>
              {s.parents.map((p, i) => (
                <ParentRow key={i}>
                  <MdFamilyRestroom size={16} color="#A855F7" />
                  <ParentName>{p.name}</ParentName>
                  {p.uid && (
                    <ParentChatBtn onClick={(e) => { e.stopPropagation(); startChatWith(p.uid, p.name, 'parent'); }}>
                      <IoChatbubbleOutline size={14} />
                    </ParentChatBtn>
                  )}
                  {p.phone && (
                    <ParentPhone href={`tel:${p.phone}`}>
                      <MdPhone size={13} /> {p.phone}
                    </ParentPhone>
                  )}
                </ParentRow>
              ))}
            </ParentSection>
          )}
        </StudentCard>
      ))}
      {!tabLoaded.students && <SpinnerWrap><Spinner /></SpinnerWrap>}
      {tabLoaded.students && students.length === 0 && !showStudentAdd && <EmptyState><p>수강생이 없습니다.</p></EmptyState>}
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 0: return renderBoard();
      case 1: return renderStudents();
      case 2: return renderAttendance();
      case 3: return renderMaterials();
      case 4: return renderAssignments();
      case 5: return renderGrades();
      case 6: return renderVideos();
      case 7: return renderLogs();
      default: return null;
    }
  };

  const handleFAB = () => {
    switch (activeTab) {
      case 0: if (canEdit) openModal('notice'); break;
      case 3: if (canUpload) openModal('material'); break;
      case 4: if (canEdit) openModal('assignment'); break;
      case 5: if (canGrade) openModal('grade'); break;
      case 6: if (canUpload) openModal('video'); break;
      case 7: if (canEdit) openModal('log'); break;
      default: break;
    }
  };

  const showFAB = (activeTab === 0 && canEdit) ||
    (activeTab === 3 && canUpload) ||
    (activeTab === 4 && canEdit) ||
    (activeTab === 5 && canGrade) ||
    (activeTab === 6 && canUpload) ||
    (activeTab === 7 && canEdit);

  const formatDate = (ts) => friendlyTimestamp(ts);

  const handleModalSubmit = () => {
    switch (modalType) {
      case 'notice': handleAddNotice(); break;
      case 'material': handleAddMaterial(); break;
      case 'assignment': handleAddAssignment(); break;
      case 'grade': handleAddGrade(); break;
      case 'video': handleAddVideo(); break;
      case 'log': handleAddLog(); break;
      default: break;
    }
  };

  if (loading) return <Container><LoadingSpinner /></Container>;
  if (!classData) return <Container><EmptyState><p>클래스를 찾을 수 없습니다.</p></EmptyState></Container>;

  return (
    <Container>
      <PageHeader
        title={classData.name}
        rightAction={<IoSettingsOutline size={22} color="white" />}
      />
      <TabGrid>
        {TABS.map((tab, i) => (
          <TabBtn key={tab} $active={activeTab === i} onClick={() => setActiveTab(i)}>
            {tab}
          </TabBtn>
        ))}
      </TabGrid>
      <Content>
        {renderContent()}
      </Content>

      {showFAB && <FAB onClick={handleFAB}><HiPlus size={24} /></FAB>}

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {modalType === 'notice' && '공지사항 작성'}
                {modalType === 'material' && '클래스매니지 자료실'}
                {modalType === 'assignment' && '수업과제 등록'}
                {modalType === 'grade' && '시험 일정 추가'}
                {modalType === 'video' && '클래스매니지 자료실'}
                {modalType === 'log' && '수업 기록 작성'}
              </ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              {modalType === 'log' ? (() => {
                const now = new Date();
                const logCalY = logForm._calY ?? now.getFullYear();
                const logCalM = logForm._calM ?? now.getMonth();
                const fd = new Date(logCalY, logCalM, 1).getDay();
                const ld = new Date(logCalY, logCalM + 1, 0).getDate();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const schedules = classData?.schedule || [];
                const logDateSet = new Set(logs.map(l => l.date));
                const setLogCal = (y, m) => setLogForm(p => ({ ...p, _calY: y, _calM: m }));

                return (
                  <>
                    <LogMiniCalWrap>
                      <LogMiniCalNav>
                        <LogMiniCalArrow onClick={() => setLogCal(logCalM === 0 ? logCalY - 1 : logCalY, logCalM === 0 ? 11 : logCalM - 1)}>
                          <IoChevronBack size={16} />
                        </LogMiniCalArrow>
                        <LogMiniCalTitle>{logCalY}년 {logCalM + 1}월</LogMiniCalTitle>
                        <LogMiniCalArrow onClick={() => setLogCal(logCalM === 11 ? logCalY + 1 : logCalY, logCalM === 11 ? 0 : logCalM + 1)}>
                          <IoChevronForward size={16} />
                        </LogMiniCalArrow>
                      </LogMiniCalNav>
                      <LogMiniCalWeek>
                        {['일','월','화','수','목','금','토'].map((d, i) => (
                          <LogMiniCalWD key={d} $sun={i === 0} $sat={i === 6}>{d}</LogMiniCalWD>
                        ))}
                      </LogMiniCalWeek>
                      <LogMiniCalGrid>
                        {Array.from({ length: fd }, (_, i) => <LogMiniCalCell key={`e${i}`} />)}
                        {Array.from({ length: ld }, (_, i) => {
                          const d = i + 1;
                          const ds = `${logCalY}-${String(logCalM + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                          const col = (fd + i) % 7;
                          const dow = DAYS_KR[new Date(logCalY, logCalM, d).getDay()];
                          const isClassDay = schedules.some(s => s.day === dow);
                          const hasLog = logDateSet.has(ds);
                          const isSelected = logForm.date === ds;
                          const isFuture = ds > todayStr;
                          return (
                            <LogMiniCalCell
                              key={d}
                              $active={isSelected}
                              $classDay={isClassDay}
                              $hasLog={hasLog}
                              $disabled={isFuture}
                              onClick={() => { if (!isFuture) setLogForm(p => ({ ...p, date: ds })); }}
                            >
                              <LogMiniCalNum $sun={col === 0} $sat={col === 6} $today={ds === todayStr} $active={isSelected}>
                                {d}
                              </LogMiniCalNum>
                              {hasLog && <LogMiniCalTag>기록</LogMiniCalTag>}
                              {!hasLog && isClassDay && !isFuture && <LogMiniCalDot />}
                            </LogMiniCalCell>
                          );
                        })}
                      </LogMiniCalGrid>
                    </LogMiniCalWrap>
                    {logForm.date && (
                      <LogSelectedDate>{getLogScheduleInfo(logForm.date)} 수업</LogSelectedDate>
                    )}
                    <FieldLabel>수업 내용 *</FieldLabel>
                    <FieldTextarea
                      placeholder=""
                      value={logForm.content}
                      onChange={e => setLogForm(p => ({ ...p, content: e.target.value }))}
                      rows={8}
                    />
                  </>
                );
              })() : modalType !== 'material' && modalType !== 'video' ? (
                <>
                  <FieldLabel>{modalType === 'grade' ? '시험 이름 *' : '제목 *'}</FieldLabel>
                  <FieldInput
                    placeholder=""
                    value={formData.title || ''}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </>
              ) : null}

              {modalType === 'notice' && (
                <>
                  <FieldLabel>내용 *</FieldLabel>
                  <FieldTextarea
                    placeholder=""
                    value={formData.content || ''}
                    onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  />
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={formData.isPinned || false}
                      onChange={e => setFormData(prev => ({ ...prev, isPinned: e.target.checked }))}
                    />
                    <span>고정 공지</span>
                  </CheckboxRow>
                </>
              )}

              {modalType === 'assignment' && (
                <>
                  <FieldLabel>설명</FieldLabel>
                  <FieldTextarea
                    placeholder=""
                    value={formData.description || ''}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <FieldLabel>마감일 *</FieldLabel>
                  <FieldDateInput
                    type="date"
                    value={formData.dueDate || ''}
                    onChange={e => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </>
              )}

              {modalType === 'grade' && (
                <>
                  <FieldLabel>시험 날짜 *</FieldLabel>
                  <FieldDateInput
                    type="date"
                    value={formData.dueDate || ''}
                    onChange={e => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                  <FieldLabel>만점</FieldLabel>
                  <FieldInput
                    type="number"
                    placeholder=""
                    value={formData.maxScore || ''}
                    onChange={e => setFormData(prev => ({ ...prev, maxScore: e.target.value }))}
                  />
                </>
              )}

              {modalType === 'material' && (
                <>
                  <SharedSearchBox
                    placeholder="자료 검색..."
                    value={sharedSearch}
                    onChange={e => setSharedSearch(e.target.value)}
                  />
                  <SharedList>
                    {sharedMaterials
                      .filter(m => !sharedSearch || (m.title || '').toLowerCase().includes(sharedSearch.toLowerCase()))
                      .map(mat => {
                        const selected = (formData.selectedMaterials || []).some(s => s.id === mat.id);
                        return (
                          <SharedItem key={mat.id} $selected={selected} onClick={() => {
                            const prev = formData.selectedMaterials || [];
                            const next = selected ? prev.filter(s => s.id !== mat.id) : [...prev, mat];
                            setFormData(p => ({ ...p, selectedMaterials: next }));
                          }}>
                            <SharedCheck $selected={selected}>{selected ? '✓' : ''}</SharedCheck>
                            {getFileExt(mat.fileName) ? (
                              <FileIconBodySm>
                                <FileIconFoldSm />
                                <FileIconExtSm $color={EXT_COLORS[getFileExt(mat.fileName)] || '#9CA3AF'}>
                                  {getFileExt(mat.fileName).toUpperCase()}
                                </FileIconExtSm>
                              </FileIconBodySm>
                            ) : (
                              <MdDescription size={32} color="#FB8C00" />
                            )}
                            <SharedInfo>
                              <SharedTitle>{mat.title}</SharedTitle>
                              <SharedMeta>{mat.category || ''}{mat.fileName ? ` · ${mat.fileName}` : ''}</SharedMeta>
                            </SharedInfo>
                          </SharedItem>
                        );
                      })}
                    {sharedMaterials.length === 0 && <EmptyState style={{ minHeight: 100 }}><p>자료실이 비어있습니다.</p></EmptyState>}
                  </SharedList>
                  {(formData.selectedMaterials || []).length > 0 && (
                    <SharedCount>{(formData.selectedMaterials || []).length}개 선택됨</SharedCount>
                  )}
                </>
              )}

              {modalType === 'video' && (
                <>
                  <SharedSearchBox
                    placeholder="영상 검색..."
                    value={sharedSearch}
                    onChange={e => setSharedSearch(e.target.value)}
                  />
                  <SharedList>
                    {sharedMaterials
                      .filter(m => m.fileType === 'video')
                      .filter(m => !sharedSearch || (m.title || '').toLowerCase().includes(sharedSearch.toLowerCase()))
                      .map(mat => {
                        const selected = (formData.selectedMaterials || []).some(s => s.id === mat.id);
                        return (
                          <SharedItem key={mat.id} $selected={selected} onClick={() => {
                            const prev = formData.selectedMaterials || [];
                            const next = selected ? prev.filter(s => s.id !== mat.id) : [...prev, mat];
                            setFormData(p => ({ ...p, selectedMaterials: next }));
                          }}>
                            <SharedCheck $selected={selected}>{selected ? '✓' : ''}</SharedCheck>
                            {getFileExt(mat.fileName) ? (
                              <FileIconBodySm>
                                <FileIconFoldSm />
                                <FileIconExtSm $color={EXT_COLORS[getFileExt(mat.fileName)] || '#9CA3AF'}>
                                  {getFileExt(mat.fileName).toUpperCase()}
                                </FileIconExtSm>
                              </FileIconBodySm>
                            ) : (
                              <MdDescription size={32} color="#FB8C00" />
                            )}
                            <SharedInfo>
                              <SharedTitle>{mat.title}</SharedTitle>
                              <SharedMeta>{mat.category || ''}{mat.fileName ? ` · ${mat.fileName}` : ''}</SharedMeta>
                            </SharedInfo>
                          </SharedItem>
                        );
                      })}
                    {sharedMaterials.filter(m => m.fileType === 'video').length === 0 && <EmptyState style={{ minHeight: 100 }}><p>영상 자료가 없습니다.</p></EmptyState>}
                  </SharedList>
                  {(formData.selectedMaterials || []).length > 0 && (
                    <SharedCount>{(formData.selectedMaterials || []).length}개 선택됨</SharedCount>
                  )}
                </>
              )}

              <ModalButtons>
                <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleModalSubmit} disabled={uploading}>
                  {uploading ? '업로드 중...' : '저장'}
                </SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ClassDetailPage;

// ============ STYLES ============

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.default};
  padding-bottom: 80px;
`;

const TabGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabBtn = styled.button`
  padding: 10px 0;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 700 : 500};
  color: ${({ $active }) => $active ? 'white' : '#6B7280'};
  background: ${({ $active }) => $active ? 'var(--color-primary)' : '#F3F4F6'};
  border: none;
  border-radius: 8px;
  transition: all 0.15s;
`;

const Content = styled.div`
  padding: 16px;
`;

// Students
const StudentTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StudentCount = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const AddStudentBtn = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  background: var(--color-primary);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
`;

const StudentAddSection = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px;
  margin-bottom: 12px;
`;

const StudentSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-bottom: 8px;
`;

const StudentAddList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const StudentAddRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:active { background: #F8FAFF; }
`;

const StudentAddInfo = styled.div`
  flex: 1;
`;

const StudentAddName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  color: #111;
`;

const StudentAddMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const StudentAddEmpty = styled.p`
  text-align: center;
  padding: 16px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const StudentChatBtn = styled.button`
  color: #4A7CFE;
  display: flex;
  align-items: center;
  padding: 4px;
  flex-shrink: 0;
  &:active { opacity: 0.6; }
`;

const ParentChatBtn = styled.button`
  color: #AB47BC;
  display: flex;
  align-items: center;
  padding: 2px;
  flex-shrink: 0;
  &:active { opacity: 0.6; }
`;

const StudentRemoveBtn = styled.button`
  color: #fff;
  background: #EF4444;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  &:hover { background: #DC2626; }
`;

const StudentCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 14px 16px;
  margin-bottom: 10px;
`;

const StudentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StudentAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const StudentAvatarDefault = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #D1D5DB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StudentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StudentName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StudentMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const ParentSection = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ParentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const ParentName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const ParentPhone = styled.a`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
`;

// Board
const NoticeBox = styled(Card)`
  background: #F1F8F6;
  margin-bottom: 16px;
`;

const NoticeHeader = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 12px;
`;

const NoticeItem = styled.div`
  padding: 8px 0;
  border-top: 1px solid #E0E0E0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoticeDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
`;

const PostCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 16px;
  margin-bottom: 10px;
`;

const PostCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const PostCardTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  flex: 1;
`;

const PostCardContent = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const PostCardMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 10px;
`;

// Attendance - Calendar
const CalendarWrap = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`;

const CalMonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const CalMonthTitle = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  min-width: 120px;
  text-align: center;
`;

const CalNavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #6B7280;
  &:hover { background: var(--color-primary-light); color: var(--color-primary); }
`;

const CalWeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #F3F4F6;
  padding-bottom: 6px;
  margin-bottom: 4px;
`;

const CalWeekCell = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 0;
  color: ${({ $sun, $sat }) => $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#9CA3AF'};
`;

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const CalDayCell = styled.div`
  min-height: 58px;
  padding: 4px 1px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  cursor: ${({ $hasSchedule }) => $hasSchedule ? 'pointer' : 'default'};
  border-radius: 8px;
  background: ${({ $selected }) => $selected ? 'var(--color-primary-light)' : 'transparent'};
  ${({ $selected }) => $selected && 'border: 1.5px solid var(--color-primary);'}
`;

const CalDayNum = styled.span`
  font-size: 14px;
  font-weight: ${({ $today }) => $today ? 700 : 600};
  color: ${({ $sun, $sat, $today }) => $today ? 'var(--color-primary)' : $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#374151'};
`;

const CalDayDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $done, $future }) => $done ? '#22C55E' : $future ? '#D1D5DB' : '#F59E0B'};
`;

const CalSummaryMini = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const CalSumText = styled.span`
  font-size: 8px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  line-height: 1.1;
`;

// Attendance Panel
const AttPanel = styled.div`
  background: white;
  border: 1.5px solid var(--color-primary);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(74,124,254,0.1);
`;

const AttPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const AttPanelTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const AttFutureMsg = styled.p`
  text-align: center;
  padding: 24px 0;
  font-size: 14px;
  color: #9CA3AF;
`;

const AttCard = styled.div`
  border-radius: 10px;
  padding: 12px;
  border: 1px solid #EAEDF3;
  background: #FAFBFC;
  margin-bottom: 8px;
`;

const AttCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 6px;
`;

const AttCardNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AttCardName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

const AttNoteToggle = styled.button`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $hasNote }) => $hasNote ? 'var(--color-primary-light)' : '#F3F4F6'};
  color: ${({ $hasNote }) => $hasNote ? 'var(--color-primary)' : '#9CA3AF'};
  font-weight: 600;
  border: 1px solid ${({ $hasNote }) => $hasNote ? 'var(--color-primary)' : 'transparent'};
`;

const AttStatusRow = styled.div`
  display: flex;
  gap: 4px;
`;

const AttStatusBtn = styled.button`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  transition: all 0.15s;
  background: ${({ $active, $color }) => $active ? $color : '#F3F4F6'};
  color: ${({ $active }) => $active ? 'white' : '#9CA3AF'};
`;

const AttNoteInput = styled.input`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  box-sizing: border-box;
  &::placeholder { color: #C4C4C4; }
  &:focus { border-color: var(--color-primary); outline: none; }
`;

const AttSaveBtn = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: var(--color-primary);
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
  &:hover { background: var(--color-primary-dark); }
`;

const AttStatusBadge = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}18;
  padding: 4px 12px;
  border-radius: 8px;
`;

const AttSelfModeNotice = styled.p`
  font-size: 13px;
  color: #6B7280;
  background: #F3F4F6;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 8px;
  text-align: center;
`;

const AttReadonlyNote = styled.p`
  font-size: 12px;
  color: #6B7280;
  margin-top: 4px;
  padding-left: 2px;
`;

const ToastOverlay = styled.div`
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
`;

const ToastBox = styled.div`
  background: #374151;
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 320px;
  line-height: 1.4;
`;

const AttMyStatus = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 0;
  text-align: center;
`;

const AttReasonText = styled.p`
  font-size: 13px;
  color: #6B7280;
  margin-top: 8px;
  padding: 8px 10px;
  background: #F9FAFB;
  border-radius: 6px;
`;

const AttReasonInput = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  font-size: 13px;
  resize: none;
  margin-top: 8px;
  &:focus { border-color: var(--color-primary); outline: none; }
`;

const SelfCheckBanner = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  text-align: center;
`;

const SelfCheckBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  background: #22C55E;
  color: white;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:disabled { opacity: 0.6; }
`;

const SelfCheckDone = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #22C55E;
  padding: 8px 0;
`;

// Materials & Videos
const MaterialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const MaterialIcon = styled.div`
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s;
  &:hover, &:active {
    opacity: 0.7;
  }
`;

const MaterialInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategoryLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  display: block;
  margin-bottom: 2px;
`;

const MaterialTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MaterialMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
`;

// Assignments
const AssignmentTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const AssignmentTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const AssignmentDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 8px;
`;

const AssignmentMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 8px;
`;

const SubmissionStats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SubmissionChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  background: ${({ $active, $color }) => $active ? $color : $color + '18'};
  color: ${({ $active, $color }) => $active ? 'white' : $color};
  border: 1px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
  transition: all 0.15s;
`;

const SubmissionDetail = styled.div`
  margin-top: 10px;
  background: #F9FAFB;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const SubmissionGroup = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid #E5E7EB;
  }
`;

const SubmissionGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  padding: 8px 12px 4px;
`;

const SubmissionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  font-size: 13px;
  border-bottom: 1px solid #F3F4F6;
  &:last-child { border-bottom: none; }
  span { color: #374151; }
`;

const SubmissionStatus = styled.span`
  font-weight: 600;
  color: ${({ $color }) => $color} !important;
  font-size: 12px;
`;

const GradeButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 10px;
`;

const GradeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  span:first-child { flex: 1; }
`;

const GradeInput = styled.input`
  width: 80px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 8px;
  text-align: center;
`;

// Common
const DeleteBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.hint};
  flex-shrink: 0;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const SpinnerWrap = styled.div`display: flex; justify-content: center; padding: 40px 0;`;
const Spinner = styled.div`width: 28px; height: 28px; border: 3px solid #E5E7EB; border-top-color: var(--color-primary); border-radius: 50%; animation: ${spin} 0.8s linear infinite;`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.hint};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const FAB = styled.button`
  position: fixed;
  bottom: 80px;
  right: max(16px, calc(50% - 220px));
  width: 52px;
  height: 52px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadow.large};
  z-index: 50;
`;

// Modals
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const GradingOverlay = styled(ModalOverlay)``;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const GradingModal = styled(Modal)``;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldDateInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  position: relative;
  &::-webkit-datetime-edit {
    color: ${({ value }) => value ? 'inherit' : 'transparent'};
  }
  &::before {
    content: ${({ value }) => !value ? "'날짜 선택'" : "none"};
    color: #9CA3AF;
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  min-height: 80px;
  resize: vertical;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  &:disabled { opacity: 0.6; }
`;

// 파일 확장자 아이콘
const FileIconBody = styled.div`
  width: 42px;
  height: 52px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 4px 10px 4px 4px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
  flex-shrink: 0;
`;

const FileIconFold = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #E5E7EB 50%, #D1D5DB 50%);
  border-radius: 0 0 0 3px;
`;

const FileIconExt = styled.span`
  font-size: 10px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  letter-spacing: -0.3px;
`;

const FileIconBodySm = styled.div`
  width: 32px;
  height: 40px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 3px 8px 3px 3px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  flex-shrink: 0;
`;

const FileIconFoldSm = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 9px;
  height: 9px;
  background: linear-gradient(135deg, #E5E7EB 50%, #D1D5DB 50%);
  border-radius: 0 0 0 2px;
`;

const FileIconExtSm = styled.span`
  font-size: 8px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  letter-spacing: -0.3px;
`;

// 공유 자료실 선택
const SharedSearchBox = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 10px;
`;

const SharedList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
`;

const SharedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  cursor: pointer;
  background: ${({ $selected }) => $selected ? 'var(--color-primary-light)' : 'white'};
  border-bottom: 1px solid #F3F4F6;
  &:last-child { border-bottom: none; }
  &:active { background: #F3F4F6; }
`;

const SharedCheck = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${({ $selected }) => $selected ? 'var(--color-primary)' : '#D1D5DB'};
  background: ${({ $selected }) => $selected ? 'var(--color-primary)' : 'white'};
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SharedInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SharedTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SharedMeta = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const SharedCount = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  margin-top: 10px;
  text-align: right;
`;

// Grade tab
const GradeStatsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const GradeStatBox = styled.div`
  flex: 1;
  text-align: center;
  padding: 8px;
  background: ${({ $bg }) => $bg || '#F5F7FA'};
  border-radius: 8px;
`;

const GradeStatValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #111827;
`;

const GradeStatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  span { flex: 1; font-size: 14px; }
`;

const ScoreInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const ScoreInput = styled.input`
  width: 100px;
  padding: 7px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  text-align: center;
  font-size: 15px;
  &:disabled { background: #F3F4F6; color: #9CA3AF; }
`;

const AbsentBtn = styled.button`
  padding: 7px 14px;
  border: 1px solid ${({ $active }) => $active ? '#EF4444' : '#D1D5DB'};
  background: ${({ $active }) => $active ? '#FEF2F2' : 'white'};
  color: ${({ $active }) => $active ? '#EF4444' : '#6B7280'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  white-space: nowrap;
`;

const SaveScoreBtnWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 12px;
  margin-top: 12px;
  margin-bottom: 8px;
`;

const SaveScoreBtn = styled.button`
  padding: 8px 24px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

// Log tab
const LogFormCard = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 16px;
  margin-bottom: 16px;
`;

const LogSubmitBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  &:disabled { opacity: 0.5; }
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const LogDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const LogContent = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
`;

// 스케줄 관리
const ScheduleSectionTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: #6B7280;
  margin: 0 0 8px;
`;
const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;
const ScheduleChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  ${({ $supplement }) => $supplement
    ? 'background:#FEE2E2;color:#DC2626;border:1px solid #FECACA;'
    : 'background:#F3F4F6;color:#374151;'}
`;
const ScheduleAddChip = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  background: none;
  color: var(--color-primary);
  border: 1px dashed var(--color-primary);
  cursor: pointer;
  &:hover { background: var(--color-primary-light); }
`;
const ChipRemoveBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  color: #9CA3AF;
  cursor: pointer;
`;
const ScheduleEmpty = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 8px;
`;
const ScheduleAddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;
const ScheduleSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 12px;
  flex-shrink: 0;
`;
const ScheduleInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 12px;
  flex: 1 1 auto;
  min-width: 80px;
  &:focus { border-color: var(--color-primary); outline: none; }
`;
const ScheduleDateInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 12px;
  flex: 0 0 95px;
  min-width: 0;
  position: relative;
  &:focus { border-color: var(--color-primary); outline: none; }
  &::-webkit-datetime-edit-year-field {
    display: none;
  }
  &::-webkit-datetime-edit-text:first-of-type {
    display: none;
  }
  &::-webkit-datetime-edit {
    color: ${({ value }) => value ? 'inherit' : 'transparent'};
  }
  &::before {
    content: ${({ value }) => !value ? "'날짜 선택'" : "none"};
    color: #9CA3AF;
    font-size: 12px;
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
  }
`;
const ScheduleAddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  border: none;
  background: var(--color-primary);
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
  &:disabled { opacity: 0.5; }
`;
const ScheduleDivider = styled.div`
  height: 1px;
  background: #F3F4F6;
  margin: 14px 0;
`;

// 수업일지 미니달력
const LogMiniCalWrap = styled.div`
  background: #F9FAFB;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
`;
const LogMiniCalNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
`;
const LogMiniCalArrow = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
`;
const LogMiniCalTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
`;
const LogMiniCalWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 4px;
`;
const LogMiniCalWD = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 4px 0;
  color: ${({ $sun }) => $sun ? '#EF4444' : ({ $sat }) => $sat ? '#3B82F6' : '#9CA3AF'};
`;
const LogMiniCalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;
const LogMiniCalCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.35 : 1};
  border-radius: 8px;
  background: ${({ $active }) => $active ? 'var(--color-primary-light)' : 'transparent'};
`;
const LogMiniCalNum = styled.span`
  font-size: 13px;
  font-weight: ${({ $active, $today }) => ($active || $today) ? 700 : 400};
  color: ${({ $active, $sun, $sat }) =>
    $active ? 'var(--color-primary)' : $sun ? '#EF4444' : $sat ? '#3B82F6' : '#374151'};
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ $today, $active }) => $today && !$active ? '#F3F4F6' : 'transparent'};
`;
const LogMiniCalTag = styled.span`
  font-size: 8px;
  color: var(--color-primary);
  font-weight: 600;
  margin-top: 1px;
`;
const LogMiniCalDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #D1D5DB;
  margin-top: 2px;
`;
const LogSelectedDate = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  padding: 8px 0 4px;
`;
